import { IFieldConfigurationObject } from "AngularFormly";
import {
  FormSubmitArgs,
  FormViewConfig,
  FormViewService,
  FormViewServiceProvider,
  vsServiceName as FormViewServiceName,
  vsProviderName as FormViewServiceProviderName,
} from "../../../records/forms/formView";
import { RecordDataSource } from "../../../records/grids";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../../records/grids/columns/columnDefinitionService";
import { Record } from "../../../records/server/crud";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../records/server/serverUtility";
import {
  GridConfiguration,
  RecordGridViewServiceProvider,
  vsProviderName as RecordGridViewServiceProviderName,
} from "../../../records/views/recordGridView";
import fieldHelpers from "../../../ui-formly/fieldHelpers";
import {
  LookupManagementConfig,
  LookupManagementServiceProvider,
} from "../../../records/lookup/lookupManagement";
import { Injectable } from "angular";
import { ColDef } from "../../../records/grids/columns/colDef";

export interface LookupConfig {
  lookupManagementServiceProviderName: string;
  additionalFields?: Injectable<
    (...args: any[]) => IFieldConfigurationObject[]
  >;
  additionalColumns?: Injectable<(...args: any[]) => ColDef[]>;
  namePlural?: string;
  nameSingular: string;
  url: string;
  name: string;
}

export function simpleLookupConfig(config: LookupConfig) {
  return [
    config.lookupManagementServiceProviderName,
    FormViewServiceProviderName,
    RecordGridViewServiceProviderName,
    (
      lookupManagementServiceProvider: LookupManagementServiceProvider,
      formViewServiceProvider: FormViewServiceProvider,
      recordGridViewServiceProvider: RecordGridViewServiceProvider
    ) =>
      lookupConfig(
        lookupManagementServiceProvider,
        formViewServiceProvider,
        recordGridViewServiceProvider,
        config
      ),
  ];
}

export function lookupConfig(
  lookupManagementServiceProvider: LookupManagementServiceProvider,
  formViewServiceProvider: FormViewServiceProvider,
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  config: LookupConfig
) {
  configureLookupManagement(lookupManagementServiceProvider, config);
  configureForm(formViewServiceProvider, config);
  recordGridViewServiceProvider.addGridConfiguration(config.name, [
    ServerUtilityName,
    ColumnDefinitionServiceName,
    "$injector",
    (
      serverUtility: ServerUtility,
      columnDefinitionService: ColumnDefinitionService,
      $injector: angular.auto.IInjectorService
    ) =>
      getGridConfig(serverUtility, columnDefinitionService, config, $injector),
  ]);
}

function configureForm(
  formViewServiceProvider: FormViewServiceProvider,
  config: LookupConfig
) {
  formViewServiceProvider.addFormViewConfig(config.name, [
    FormViewServiceName,
    "$injector",
    (
      formViewService: FormViewService,
      $injector: angular.auto.IInjectorService
    ): FormViewConfig => {
      const additionalFields = config.additionalFields
        ? $injector.invoke(config.additionalFields)
        : [];
      return {
        fields: [
          fieldHelpers.simpleStringField({
            key: "Name",
            name: "Name",
            label: config.nameSingular,
            required: true,
          }),
          ...additionalFields,
          fieldHelpers.simpleBoolField({
            key: "Disabled",
            name: "Disabled",
            label: "Disabled?",
            defaultValue: false,
          }),
        ],
        name: config.name,
        submitForm: (args: FormSubmitArgs) => {
          const model = args.model;
          return formViewService.saveForm({
            formObject: model,
            formViewController: args.formViewController,
            url: config.url,
          });
        },
        title: (model: Record) =>
          (model.Id ? "Edit " : "Create ") + config.nameSingular,
      };
    },
  ]);
}

function configureLookupManagement(
  lookupManagementServiceProvider: LookupManagementServiceProvider,
  config: LookupConfig
) {
  lookupManagementServiceProvider.addLookup(config.name, {
    displayName: config.nameSingular,
    config: [
      ServerUtilityName,
      (serverUtility: ServerUtility): LookupManagementConfig => {
        return {
          fetchFormModel: (id: number) =>
            serverUtility.querySingle({
              url: config.url,
              query: { Id: id },
            }),
          baseUrl: config.url,
        };
      },
    ],
  });
}

function getGridConfig(
  serverUtility: ServerUtility,
  columnDefinitionService: ColumnDefinitionService,
  config: LookupConfig,
  $injector: angular.auto.IInjectorService
): GridConfiguration {
  const additionalColumns = config.additionalColumns
    ? $injector.invoke(config.additionalColumns)
    : [];
  return {
    columns: [
      columnDefinitionService.getStringColDef({
        field: "Name",
        header: config.nameSingular,
      }),
      ...additionalColumns,
      columnDefinitionService.getBooleanColDef({
        field: "Disabled",
      }),
    ],
    gridDataSource: new RecordDataSource(serverUtility, config.url),
    defaultSort: "Name",
    namePlural: config.namePlural,
    nameSingular: config.nameSingular,
    showAddButton: true,
    showAddButtonPermission: "machineLookupModify",
    showSelectButtons: false,
  };
}
